import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
    Plus,
    Search,
    Pencil,
    Trash2,
    Copy,
    ChevronLeft,
    ChevronRight,
    Truck,
    Upload,
    RefreshCcw
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import { PlanMaestroForm } from './PlanMaestroForm';
import { VehicleAssignmentModal } from './VehicleAssignmentModal';
import { DeletePlanModal } from './DeletePlanModal';
import { PlanMaestroImportModal } from './PlanMaestroImportModal';
import type { PlanMaestro } from '../../types/planMaestro';

export default function GestionPlanes() {
    const [planes, setPlanes] = useState<PlanMaestro[]>(() => {
        try {
            const cached = sessionStorage.getItem('vilox_cache_list_planes');
            return cached ? JSON.parse(cached) : [];
        } catch { return []; }
    });
    const [loading, setLoading] = useState(planes.length === 0);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isAssignmentOpen, setIsAssignmentOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState<number | undefined>(undefined);
    const [selectedPlan, setSelectedPlan] = useState<PlanMaestro | null>(null);
    const [viewType, setViewType] = useState<'company' | 'system'>('company');

    // Fetch planes
    const fetchPlanes = async (typeOverride?: 'company' | 'system', force: boolean = false) => {
        try {
            if (planes.length === 0 || force) setLoading(true);
            const type = typeOverride || viewType;
            const data = await maintenanceService.getPlanesMaestros(type);
            setPlanes(data);
            try {
                sessionStorage.setItem('vilox_cache_list_planes', JSON.stringify(data));
            } catch (e) { console.warn(e); }
        } catch (error) {
            console.error('Error fetching planes:', error);
            toast.error('Error al cargar los planes maestros');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        // 1. Limpiar caché en sessionStorage
        sessionStorage.removeItem('vilox_cache_list_planes');
        
        // 2. Forzar recarga
        await fetchPlanes(viewType, true);
        
        // 3. Notificar éxito
        toast.success('Planes actualizados');
    };

    useEffect(() => {
        fetchPlanes();
    }, [viewType]);

    // Filter planes
    const filteredPlanes = planes.filter(plan =>
        plan.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (plan.tipo_operacion && plan.tipo_operacion.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Handlers
    const handleCreate = () => {
        setSelectedPlanId(undefined);
        setIsFormOpen(true);
    };

    const handleEdit = (id: number) => {
        setSelectedPlanId(id);
        setIsFormOpen(true);
    };

    const handleDelete = (plan: PlanMaestro) => {
        setSelectedPlan(plan);
        setIsDeleteModalOpen(true);
    };

    const handleFormSuccess = () => {
        setIsFormOpen(false);
        fetchPlanes();
    };

    const handleAssign = (plan: PlanMaestro) => {
        setSelectedPlan(plan);
        setIsAssignmentOpen(true);
    };

    const handleDuplicate = async (id: number) => {
        try {
            setLoading(true);
            await maintenanceService.duplicatePlanMaestro(id);
            toast.success('Plan duplicado correctamente');
            fetchPlanes();
        } catch (error) {
            console.error('Error duplicating plan:', error);
            toast.error('Error al duplicar el plan');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <Card className="shadow-sm">
                <CardHeader className="p-4 pb-2">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <CardTitle className="text-2xl font-bold text-gray-800 tracking-tight">
                                Planes de Mantenimiento Maestros
                            </CardTitle>
                            <p className="text-sm text-gray-500 font-medium mt-1">
                                Gestiona y configura los planes de mantenimiento para tu flota
                            </p>
                        </div>
                        <div className="flex bg-gray-100 p-1 rounded-lg self-start border border-gray-200">
                            <button
                                className={`h-8 rounded-md px-5 text-xs font-semibold transition-all ${
                                    viewType === 'company'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                                }`}
                                onClick={() => setViewType('company')}
                            >
                                Mis Planes
                            </button>
                            <button
                                className={`h-8 rounded-md px-5 text-xs font-semibold transition-all ${
                                    viewType === 'system'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                                }`}
                                onClick={() => setViewType('system')}
                            >
                                Plantillas
                            </button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                            <Input
                                placeholder="Buscar planes..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                size="icon"
                                title="Recargar planes"
                                disabled={loading}
                                onClick={handleRefresh}
                                className="h-10 w-10 border-gray-200 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all"
                            >
                                <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            </Button>

                            {viewType === 'company' && (
                                <>
                                    <Button
                                        variant="outline"
                                        className="h-10 border-blue-200 text-blue-600 hover:bg-blue-50"
                                        onClick={() => setIsImportModalOpen(true)}
                                    >
                                        <Upload className="h-4 w-4 mr-2" />
                                        Cargar PDF / Excel
                                    </Button>
                                    <Button
                                        className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
                                        onClick={handleCreate}
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Nuevo Plan
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Tabla */}
                    <div className="rounded-md border border-gray-100 overflow-hidden">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-100">
                                <tr>
                                    <th className="p-4 font-medium">Nombre</th>
                                    <th className="p-4 font-medium">Modelo</th>
                                    <th className="p-4 font-medium">Motor</th>
                                    <th className="p-4 font-medium">Tipo Operación</th>
                                    <th className="p-4 font-medium text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <tr key={i} className="animate-pulse">
                                            <td className="p-4"><div className="h-4 bg-gray-200 rounded w-3/4"></div></td>
                                            <td className="p-4"><div className="h-4 bg-gray-200 rounded w-1/2"></div></td>
                                            <td className="p-4"><div className="h-4 bg-gray-200 rounded w-1/2"></div></td>
                                            <td className="p-4"><div className="h-6 bg-gray-200 rounded-full w-20"></div></td>
                                            <td className="p-4 text-right"><div className="h-8 bg-gray-200 rounded w-24 ml-auto"></div></td>
                                        </tr>
                                    ))
                                ) : filteredPlanes.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="p-8 text-center text-gray-500">
                                            No se encontraron planes
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPlanes.map((plan) => (
                                        <tr key={plan.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="p-4 font-medium text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    {plan.nombre}
                                                    {!plan.empresa_id && (
                                                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-100">
                                                            SISTEMA
                                                        </Badge>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4 text-gray-600">
                                                {plan.modelo_vehiculo || '-'}
                                            </td>
                                            <td className="p-4 text-gray-600">
                                                {plan.motor || '-'}
                                            </td>
                                            <td className="p-4">
                                                <Badge
                                                    variant="secondary"
                                                    className={`${plan.tipo_operacion === 'CARRETERA'
                                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                                        }`}
                                                >
                                                    {plan.tipo_operacion || 'N/A'}
                                                </Badge>
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex justify-end gap-1.5">
                                                    {viewType === 'company' ? (
                                                        <>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 px-3 gap-1.5 text-blue-600 border-blue-200 hover:text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                                                                onClick={() => handleEdit(plan.id)}
                                                                title="Editar"
                                                            >
                                                                <Pencil className="h-3.5 w-3.5" />
                                                                <span className="text-xs font-medium">Editar</span>
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 px-3 gap-1.5 text-gray-600 border-gray-200 hover:text-gray-800 hover:bg-gray-100"
                                                                onClick={() => handleDuplicate(plan.id)}
                                                                title="Clonar"
                                                            >
                                                                <Copy className="h-3.5 w-3.5" />
                                                                <span className="text-xs font-medium">Clonar</span>
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 px-3 gap-1.5 text-orange-600 border-orange-200 hover:text-orange-700 hover:bg-orange-50"
                                                                onClick={() => handleAssign(plan)}
                                                                title="Vincular Vehículos"
                                                            >
                                                                <Truck className="h-3.5 w-3.5" />
                                                                <span className="text-xs font-medium">Vincular</span>
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 px-3 gap-1.5 text-red-500 border-red-200 hover:text-red-600 hover:bg-red-50"
                                                                onClick={() => handleDelete(plan)}
                                                                title="Eliminar"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                <span className="text-xs font-medium">Eliminar</span>
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 flex items-center gap-2 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200"
                                                            onClick={() => handleDuplicate(plan.id)}
                                                            title="Usar Plantilla"
                                                        >
                                                            <Copy className="h-3.5 w-3.5" />
                                                            <span className="text-xs font-semibold">Usar Plantilla</span>
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!loading && filteredPlanes.length > 0 && (
                        <div className="flex items-center justify-end space-x-2 py-4">
                            <Button variant="outline" size="sm" disabled>
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" className="bg-blue-50 text-blue-600 border-blue-200">
                                1
                            </Button>
                            <Button variant="outline" size="sm" disabled>
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {isFormOpen && (
                <PlanMaestroForm
                    open={isFormOpen}
                    onClose={() => setIsFormOpen(false)}
                    onSuccess={handleFormSuccess}
                    planId={selectedPlanId}
                />
            )}

            {isAssignmentOpen && selectedPlan && (
                <VehicleAssignmentModal
                    open={isAssignmentOpen}
                    onClose={() => setIsAssignmentOpen(false)}
                    planId={selectedPlan.id}
                    planNombre={selectedPlan.nombre}
                />
            )}

            {selectedPlan && (
                <DeletePlanModal
                    open={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onSuccess={() => {
                        fetchPlanes();
                        setIsDeleteModalOpen(false);
                    }}
                    planId={selectedPlan.id}
                    planNombre={selectedPlan.nombre}
                />
            )}
            {isImportModalOpen && (
                <PlanMaestroImportModal
                    isOpen={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                    onSuccess={() => {
                        fetchPlanes();
                        setIsImportModalOpen(false);
                    }}
                />
            )}
        </div>
    );
}
